import styled, { css } from 'styled-components';
import { media } from '@simplywallst/ui-core';

interface Props {
  visible: boolean;
}

export const Wrapper = styled('span').attrs<Props>(({ visible }) => ({
  style: {
    display: visible ? 'block' : 'none',
  },
}))<Props>`
  z-index: 9999;
  position: absolute;
  left: 0;
  width: 100%;
  bottom: 0;
  display: none;
`;

interface ListProps {
  align: 'left' | 'right';
  searchable?: boolean;
  width?: number;
}

export const List = styled('ul')<ListProps>`
  position: absolute;
  top: ${({ theme, searchable }) => (searchable ? 0 : theme.x1spacePx)}px;
  ${({ align }) => (align === 'left' ? `left: 0;` : `right: 0;`)}
  width: 100%;
  min-width: ${({ width }) => width || 200}px;
  padding: ${({ theme }) => theme.x1spacePx / 2}px 0;
  border-radius: ${({ theme, searchable }) =>
    searchable
      ? `0 0 ${theme.x1spacePx / 2}px
          ${theme.x1spacePx / 2}px`
      : `${theme.x1spacePx / 2}px`};
  background: white;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain; // Avoid scrolling page when limits are reached
  box-shadow: 0px 2px 8px ${({ theme }) => theme.color.blackRGBA(0.25)};
`;

interface Theme {
  underline?: boolean;
  selected?: boolean;
  hovered?: boolean;
  disabled?: boolean;
  level?: string;
  displayTopDivider?: boolean;
  suppressedSvgColor?: boolean;
}

const hover = css`
  && {
    background: ${({ theme }) => theme.color.brand01};
    color: ${({ theme }) => theme.color.white};
    * {
      fill: ${({ theme }) => theme.color.white};
    }
  }
`;

const level1Style = css`
  ${({ theme }) => theme.typography.smallStrong};
  padding-left: ${({ theme }) => theme.x5spacePx}px;
  margin: ${({ theme }) => theme.x1spacePx}px 0;
`;

const level2Style = css`
  ${({ theme }) => theme.typography.xSmall};
  padding-left: ${({ theme }) => theme.x6spacePx}px;
`;

const level3Style = css`
  ${({ theme }) => theme.typography.xSmall};
  padding-left: ${({ theme }) => theme.x7spacePx}px;
`;

export const Item = styled('li')<Theme>`
  position: relative;
  white-space: nowrap;
  ${({ theme, selected }) =>
    selected ? theme.typography.xSmallStrong : theme.typography.xSmall};
  &&& {
    background: ${({ theme, selected }) =>
      selected ? theme.color.white : 'transparent'};
    color: ${({ theme }) => theme.color.spaceGray};
  }
  cursor: ${({ disabled }) => (disabled ? `default` : `pointer`)};
  padding: ${({ theme }) =>
    `${theme.x1spacePx}px 12px ${theme.x1spacePx}px ${theme.x5spacePx - 4}px`};
  display: block;
  color: ${({ theme, disabled }) =>
    !disabled ? theme.color.spaceGray : theme.color.spaceGrayRGBA(0.3)};
  text-decoration: none;
  user-select: none;
  &:not(:last-child) {
    border-bottom: ${({ underline, theme }) =>
      underline ? `1px solid ${theme.color.spaceGrayRGBA(0.1)}` : 'none'};
  }
  ${({ hovered, disabled }) => hovered && !disabled && hover}
  ${media.lgUp`
    &:hover {
      ${({ disabled }) => !disabled && hover}
    }
  `};
  ${({ level }) => level === '1' && level1Style}
  ${({ level }) => level === '2' && level2Style}
  ${({ level }) => level === '3' && level3Style}
  border-top: ${({ displayTopDivider, theme }) =>
    displayTopDivider ? `1px solid ${theme.color.spaceGrayRGBA(0.1)}` : 'none'};
`;

export const Prefix = styled('span')<Theme>`
  position: absolute;
  left: 8px;
  top: 5px;
  ${media.lgUp`
    left: 8px;
    top: 6px;
  `};
  &&& {
    > svg {
      * {
        fill: ${({ theme, suppressedSvgColor }) =>
          suppressedSvgColor ? theme.color.spaceGray : theme.color.brand01};
      }
    }
  }
`;
